import { useState, useContext } from "react";
import PropTypes from "prop-types";
import { useLocation } from "@gatsbyjs/reach-router";

import { CartContext } from "@context/CartProvider";
import { useTracking } from "@context/Tracking";
import { useCurrentCountryCode } from "@hooks/usePrices";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { gaCustomEvent } from "@helpers/browser";
import { cn } from "@helpers/general";
import { ComponentType } from "@constants/ComponentType";

import { ALLink, ALButtonV2 } from "@components/ALComponents";

import RadioVariants from "@components/RadioVariants";

import IconClose from "@svgs/close-delete.svg";

function QuickAddToCart({
  onQuickAddToCart,
  product,
  collectionTitle,
  componentType,
  className,
  isMobile,
  atcButtonDisabled = false,
}) {
  const { toggleCart } = useContext(CartContext);
  const countryCode = useCurrentCountryCode();
  const { trackRecommendedProductsATC } = useTracking();
  const [showVariants, setShowVariants] = useState(false);
  const location = useLocation();

  const isByob =
    componentType === ComponentType.CP_BYOB || componentType === ComponentType.CP_BYOB_QUICKVIEW;
  const isUpsell = componentType === ComponentType.SIDE_CART_UPSELL;

  /**
   * Show/Hide Variants
   */
  const handleToggleVariants = () => {
    setShowVariants(!showVariants);
  };

  const addToCart = ({ selectedVariant = null } = {}) => {
    const eventLabel = `${collectionTitle ?? componentType} - ${product.title}`;
    gaCustomEvent("Quick add to cart", "Button click", eventLabel);
    onQuickAddToCart({ selectedVariant });
    if (componentType === ComponentType.PRODUCT_RECOMMENDATIONS) {
      trackRecommendedProductsATC({ type: "add_to_cart", handles: [product.handle] });
    }
  };

  const handleAddToCartClick = () => {
    if (product.variants.length > 1 && product.productType === "Rings") {
      handleToggleVariants();
    } else {
      addToCart({ selectedVariant: product.variants[0] });
    }
  };

  /**
   * Change Variant
   * @param {*} variant
   */
  const handleOnChangeSize = (variant) => {
    addToCart({ selectedVariant: variant });
    setShowVariants(false);
  };

  let viewProductButtonText = null;
  if (!isUpsell) {
    if (product.variants.length > 1 && product.productType !== "Rings") {
      viewProductButtonText = "View Product";
    } else if (product.tags?.includes("CUSTOMIZABLE")) {
      viewProductButtonText = "Customize It";
    }
  }
  if (location.pathname === "/pages/wishlist") {
    return null;
  }

  if (showVariants) {
    return (
      <div className={`${cn(className)}`}>
        <div
          className={cn(
            "bg-white rounded-[4px] top-0 left-0 z-10 pt-[35px] px-[15px] pb-[15px]",
            isUpsell
              ? "absolute px-[10px] py-[15px] inset-[10px] flex items-center justify-center"
              : "relative border"
          )}
        >
          <button
            className={cn("absolute top-[10px] right-[10px] z-20", isUpsell && "top-0 right-0")}
            type="button"
            onClick={handleToggleVariants}
          >
            <IconClose width="12" height="12" fill="black" />
          </button>
          <div className="flex flex-wrap gap-[10px]">
            {product?.variants.map((variant, index) => (
              <RadioVariants
                key={index}
                variant={variant}
                product={{ id: product?.shopifyId }}
                handleOnChangeSize={handleOnChangeSize}
                countryCode={countryCode}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (viewProductButtonText) {
    return (
      <div className={`${cn(className)}`}>
        <ALButtonV2
          type="button"
          variant="outline"
          size={isMobile ? "small" : "medium"}
          fullWidth
          onClick={() => (isUpsell ? toggleCart(false) : null)}
        >
          <ALLink to={`/products/${product.handle}`}>{viewProductButtonText}</ALLink>
        </ALButtonV2>
      </div>
    );
  }

  return (
    <div className={`${cn(className)}`}>
      <ALButtonV2
        type="button"
        variant="outline"
        size={isMobile ? "small" : "medium"}
        className={cn(isByob && "h-[35px] typo-body")}
        fullWidth
        onClick={handleAddToCartClick}
        disabled={atcButtonDisabled}
      >
        {isByob ? "Add to Bundle" : "Add to Cart"}
      </ALButtonV2>
    </div>
  );
}

QuickAddToCart.propTypes = {
  onQuickAddToCart: PropTypes.func.isRequired,
  product: PropTypes.shape({
    handle: PropTypes.string,
    shopifyId: PropTypes.string,
    title: PropTypes.string,
    productType: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
      })
    ),
  }).isRequired,
  collectionTitle: PropTypes.string,
  componentType: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isMobile: PropTypes.bool,
  atcButtonDisabled: PropTypes.bool,
};

export default withALErrorBoundary({
  name: "QuickAddToCart",
  priority: "P1",
})(QuickAddToCart);
